import { defineMessage, IntlShape, MessageDescriptor } from 'react-intl';
import { Customer, PersonalData, WidgetConfig, WidgetId } from '@algoan/dashboard/model';

export const isWidgetVisible = (widget: WidgetId, config: WidgetConfig): boolean => !!config[widget];

export type LiteralObject = {
  [key: string]: number | boolean | string | LiteralObject | undefined | null;
};

const pathSeparator = '.';

export const getValueByPath = (path: string, object?: LiteralObject | null): number | boolean | string | undefined => {
  const pathFields = path.split(pathSeparator);
  const [firstField, ...otherFields] = pathFields;

  if (pathFields.length === 1) {
    const value = object?.[firstField];
    if (typeof value === 'object' && value !== null)
      throw new Error(
        `Field '${firstField}' in ${JSON.stringify(
          object,
        )} should be a boolean, a string or number, but it is ${typeof value}`,
      );

    return value === null ? undefined : (value as number | boolean | string);
  } else {
    const nestedObject = object?.[firstField];
    if (nestedObject === undefined) return undefined;

    if (typeof nestedObject !== 'object')
      throw new Error(
        `Field '${firstField}' in ${JSON.stringify(object)} should be an object, but it is ${typeof nestedObject}`,
      );

    return getValueByPath(otherFields.join(pathSeparator), nestedObject);
  }
};

export const getPersonalDataValue = (
  personalData: PersonalData,
  customer: Customer,
  intl: IntlShape,
): string | MessageDescriptor => {
  const value = getValueByPath(personalData.path, customer as unknown as LiteralObject);

  if (value === undefined)
    return defineMessage({
      id: 'widgets.identity.not_determined',
      defaultMessage: 'Not determined',
      description: 'Undefined property in application',
    });

  switch (personalData.type) {
    case 'date':
      const dateValue = new Date(value.toString());

      return intl.formatDate(dateValue, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    case 'number':
    case 'string':
      return value.toString();
    case 'boolean':
      const booleanValue = value as boolean;
      return booleanValue
        ? defineMessage({ id: 'widgets.identity.yes', defaultMessage: 'Yes' })
        : defineMessage({ id: 'widgets.identity.no', defaultMessage: 'No' });
    case 'enum':
      const key = value as string;
      const enumValue = personalData.values[key];
      return enumValue ?? value.toString();
  }
};
